import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import SiteFooter from '@/components/SiteFooter';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';

const BACKEND_URL = '';

/**
 * Public renderer for a single governance document (Terms / Privacy / etc.).
 * Renders markdown-ish body with light formatting — headings, bold, links,
 * paragraphs. Good enough for legal text without pulling in a md library.
 */
function renderBody(body = '') {
  // Very small markdown converter — headings, paragraphs, bold, links.
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const lines = body.split(/\r?\n/);
  const out = [];
  let para = [];
  const flushPara = () => {
    if (para.length === 0) return;
    let text = escape(para.join(' '));
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/_([^_]+)_/g, '<em>$1</em>');
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-brand-navy underline hover:text-brand-dark" target="_blank" rel="noopener noreferrer">$1</a>');
    out.push(`<p class="text-slate-600 leading-relaxed mb-4">${text}</p>`);
    para = [];
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); continue; }
    if (line.startsWith('### ')) { flushPara(); out.push(`<h3 class="text-base font-semibold text-slate-800 mt-4 mb-2">${escape(line.slice(4))}</h3>`); continue; }
    if (line.startsWith('## ')) { flushPara(); out.push(`<h2 class="text-lg font-bold text-slate-800 mt-6 mb-2">${escape(line.slice(3))}</h2>`); continue; }
    if (line.startsWith('# ')) { flushPara(); out.push(`<h1 class="text-2xl font-bold text-slate-900 mt-2 mb-4">${escape(line.slice(2))}</h1>`); continue; }
    para.push(line);
  }
  flushPara();
  return out.join('\n');
}

export default function GovernancePage({ user }) {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/governance/${slug}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setDoc(d))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid={`governance-page-${slug}`}>
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6 flex-1">
        <div className="container mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-navy mb-4">
            <ArrowLeft className="h-3 w-3" /> Back
          </Link>
          {loading ? (
            <Card className="p-8 text-center text-sm text-slate-400">Loading…</Card>
          ) : !doc ? (
            <Card className="p-8 text-center">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Document not found.</p>
              <Button asChild variant="outline" className="mt-3" size="sm"><Link to="/">Go home</Link></Button>
            </Card>
          ) : (
            <Card className="p-6 md:p-10" data-testid="governance-doc">
              <div className="mb-6 pb-4 border-b border-slate-100">
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="governance-title">{doc.title}</h1>
                {doc.updated_at && (
                  <p className="text-xs text-slate-400 mt-2">
                    Last updated {new Date(doc.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {doc.version ? ` · Version ${doc.version}` : ''}
                  </p>
                )}
              </div>
              <div
                className="prose-sm max-w-none"
                data-testid="governance-body"
                dangerouslySetInnerHTML={{ __html: renderBody(doc.body) }}
              />
            </Card>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
